import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { IconButton, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { soaApi, schemesApi } from '@/services/api';
import {
    ALL_VERIFIED,
    SoaDisclaimerMode,
    SoaDisclaimerStatus,
    SoaDocument,
    SoaEmailStatus,
    SoaReportVariant,
    SoaUploadSummary,
} from '@/types/soa';
import { formatDate, formatDateTime, formatSchemeLabel } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { FilterOption } from '@/components/utils/filters';
import { FilterSelect, FilterAsyncSelect, Button, Pill, ActiveStatus, confirmColors } from '@/components/ui';
import type { PillTone } from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import styles from './SoaList.module.css';
import { useTableState } from '@/hooks/useTableState';
import { useScope } from '@/hooks/useScope';
import { useAuth } from '@/contexts/AuthContext';
import { useClientApi } from '@/hooks/useClientApi';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { SOA_SEND_EMAIL } from '@/constants/scopeCodes';
import SoaSendModal from './SoaSendModal';
import SoaDisclaimerBar from './SoaDisclaimerBar';
import SoaDownloadDisclaimerDialog from './SoaDownloadDisclaimerDialog';
import SoaUploadDialog from './SoaUploadDialog';
import SoaAssignDialog from './SoaAssignDialog';
import SoaMissingClientsDialog from './SoaMissingClientsDialog';
import SoaRowActionsMenu from './SoaRowActionsMenu';
import { monthEndDate, previousMonthValue } from './monthEnd';
import type { SoaSendJobState } from '@/types/soa';

const REPORT_VARIANT_LABEL: Record<SoaReportVariant, string> = {
    two_page_disclaimer: '2Page_Disclaimer',
    full_report: 'FullReport',
};

const MATCH_STATUS_OPTIONS: FilterOption[] = [
    { value: 'unmatched', label: 'Unassigned Only' },
];
const VERIFICATION_OPTIONS: FilterOption[] = [
    { value: 'unverified', label: 'Not Fully Verified' },
    { value: 'verified', label: 'Fully Verified' },
];
const EMAIL_STATUS_OPTIONS: FilterOption[] = [
    { value: 'not_sent', label: 'Not Sent' },
    { value: 'queued', label: 'Queued' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
    { value: 'bounced', label: 'Bounced' },
];
const CLIENT_ACTIVE_OPTIONS: FilterOption[] = [
    { value: 'true', label: 'Active Only' },
    { value: 'false', label: 'Inactive Only' },
];

const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
};

const defaultDisclaimerMode = (status: SoaDisclaimerStatus | null): SoaDisclaimerMode =>
    status?.has_custom_text ? 'text' : status?.exists ? 'pdf' : 'text';

const getErrorMessage = (err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
        return err.response?.data?.detail || fallback;
    }
    return fallback;
};

const runDownload = async (fetchBlob: () => Promise<Blob>, filename: string, errorText: string): Promise<boolean> => {
    try {
        downloadBlob(await fetchBlob(), filename);
        return true;
    } catch (err) {
        Swal.fire('Download failed', getErrorMessage(err, errorText), 'error');
        return false;
    }
};

const renderCcEmail = (label: string, email: string | null) => (
    <span className={email ? styles.emails : styles['cc-missing']}>
        {email || `${label}: not set`}
    </span>
);

/** Pill tone per SOA email status; any other status (not sent, queued…) is neutral. */
const EMAIL_STATUS_TONE: Partial<Record<string, PillTone>> = {
    // Claimed by a running send job — not an outcome yet, so neither good nor bad.
    queued: 'info',
    sent: 'positive',
    failed: 'critical',
    // Amber rather than red: the send itself worked, and the fix is the client's address.
    bounced: 'warning',
};
const emailStatusTone = (status: string | null | undefined): PillTone =>
    (status ? EMAIL_STATUS_TONE[status] : undefined) ?? 'neutral';

/** Wrapped in a span because MUI attaches its listeners to the child, and a
 *  disabled button emits no events — losing the tooltip where it matters most. */
const ButtonTip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
    <Tooltip title={title}>
        <span>{children}</span>
    </Tooltip>
);

const describeUpload = (summary: SoaUploadSummary): string => {
    const parts = [`${summary.created} stored`, `${summary.matched} matched`];
    if (summary.updated) parts.push(`${summary.updated} replaced`);
    if (summary.unmatched) parts.push(`${summary.unmatched} unmatched`);
    if (summary.errors.length) parts.push(`${summary.errors.length} failed`);
    return parts.join(' · ');
};

const SoaList: React.FC = () => {
    const { getClients } = useClientApi();

    const loadClientOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
        try {
            const response = await getClients({ search: inputValue, page_size: 500 });
            return response.results.map(c => ({
                value: c.id.toString(),
                label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`}`,
            }));
        } catch {
            return [];
        }
    }, [getClients]);

    const loadSchemeOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
        try {
            const response = await schemesApi.getSchemes({
                search: inputValue,
                page_size: 50,
                is_active: true,
                ordering: 'code',
            });
            return response.results.map(s => ({ value: s.id.toString(), label: formatSchemeLabel(s) }));
        } catch {
            return [];
        }
    }, []);

    const [documents, setDocuments] = useState<SoaDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [month, setMonth] = useState(previousMonthValue);
    const [matchStatus, setMatchStatus] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('');
    const [emailStatusFilter, setEmailStatusFilter] = useState('');
    const [clientActive, setClientActive] = useState('');
    const [selectedClient, setSelectedClient] = useState<FilterOption | null>(null);
    const [selectedScheme, setSelectedScheme] = useState<FilterOption | null>(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [missingOpen, setMissingOpen] = useState(false);
    const [assigning, setAssigning] = useState<SoaDocument | null>(null);
    // anchorEl and document are always set and cleared together, so one state.
    const [menu, setMenu] = useState<{ anchorEl: HTMLElement; document: SoaDocument } | null>(null);
    const [checkingStatus, setCheckingStatus] = useState(false);
    // Full rows, not just ids: a cross-page selection has to keep the documents the
    // send modal counts (unassigned, already sent, unverified) once they scroll away.
    const [selectedById, setSelectedById] = useState<Map<number, SoaDocument>>(new Map());
    const [selectingAll, setSelectingAll] = useState(false);
    const [sending, setSending] = useState(false);
    // The one send run allowed at a time, polled while live: it is what disables Send
    // for everyone, not just the tab that started it.
    const [sendJob, setSendJob] = useState<SoaSendJobState | null>(null);
    const isSendLive = sendJob !== null;
    const [sendOpen, setSendOpen] = useState(false);
    const [sendRecipients, setSendRecipients] = useState('');
    const [force, setForce] = useState(false);
    const [disclaimerStatus, setDisclaimerStatus] = useState<SoaDisclaimerStatus | null>(null);
    // Defaults to 'pdf' — preserves today's behaviour until the sender opts into text.
    const [disclaimerMode, setDisclaimerMode] = useState<SoaDisclaimerMode>('pdf');
    const [disclaimerText, setDisclaimerText] = useState('');
    // Full Report (quarterly) vs 2 Pages + Disclaimer — chosen explicitly here rather
    // than inferred from period_end, so the sender confirms it every time.
    const [quarterly, setQuarterly] = useState(false);
    const [sendSubject, setSendSubject] = useState('');
    const [sendBody, setSendBody] = useState('');
    const [templateVariables, setTemplateVariables] = useState<string[]>([]);
    // The 2-Pages-+-Disclaimer download's own context-edit step — separate from the
    // send modal's state, since it is a one-off edit for a single document.
    const [downloadDialogDocument, setDownloadDialogDocument] = useState<SoaDocument | null>(null);
    const [downloadMode, setDownloadMode] = useState<SoaDisclaimerMode>('pdf');
    const [downloadText, setDownloadText] = useState('');
    const [downloading, setDownloading] = useState(false);
    const canSendEmail = useScope(SOA_SEND_EMAIL);
    const { user } = useAuth();
    // Advisory only — the server decides test mode, so this changes wording, never
    // who actually receives the statements.
    const isTestMode = import.meta.env.DEV;

    const {
        currentPage, pageSize, ordering, searchQuery, debouncedSearch,
        setTotalCount, onSort, onSearchChange, onPageChange, resetPage,
        totalCount, totalPages, sortField, sortDir,
    } = useTableState({
        defaultSortField: 'period_end',
        defaultSortDir: 'desc',
        searchDebounceMs: 400,
        pageSize: 50,
    });

    // fa_source is not sent: the backend defaults to PMS, the only mandate SOAs are
    // issued for. Unassigned documents come back regardless.
    // Kept separate from `ordering` because the selection is cleared on a change to
    // these and must survive a re-sort.
    const filters = useMemo(() => ({
        period_end: month ? monthEndDate(month) : undefined,
        unmatched: matchStatus === 'unmatched' ? ('true' as const) : undefined,
        verification: (verificationStatus || undefined) as 'verified' | 'unverified' | undefined,
        email_status: (emailStatusFilter || undefined) as SoaEmailStatus | undefined,
        client_active: (clientActive || undefined) as 'true' | 'false' | undefined,
        client_master: selectedClient ? parseInt(selectedClient.value, 10) : undefined,
        scheme: selectedScheme ? parseInt(selectedScheme.value, 10) : undefined,
        search: debouncedSearch || undefined,
    }), [month, matchStatus, verificationStatus, emailStatusFilter, clientActive, selectedClient, selectedScheme, debouncedSearch]);

    const filterParams = useMemo(() => ({ ...filters, ordering }), [filters, ordering]);

    const load = useCallback(async (isCancelled?: () => boolean) => {
        setLoading(true);
        setError(null);
        try {
            const response = await soaApi.getDocuments({
                ...filterParams,
                page: currentPage,
                page_size: pageSize,
            });
            if (isCancelled?.()) return;
            setDocuments(response.results);
            setTotalCount(response.count);
        } catch (err) {
            if (isCancelled?.()) return;
            setError(getErrorMessage(err, 'Failed to load SOA documents'));
        } finally {
            if (!isCancelled?.()) setLoading(false);
        }
    }, [filterParams, currentPage, pageSize, setTotalCount]);

    useEffect(() => {
        let cancelled = false;
        load(() => cancelled);
        return () => { cancelled = true; };
    }, [load]);

    // Cleared on a filter change, since that can hide rows a stale selection still
    // holds. Deliberately NOT on a sort or page change — those only reorder what is
    // already in scope, which is what lets a selection span pages.
    useEffect(() => {
        setSelectedById(new Map());
    }, [filters]);

    // Poll only while a run is live, and reload the rows when one ends so the final
    // Email Status is on screen without the sender refreshing.
    // Through a ref so the poll's lifecycle depends only on whether a run is live:
    // load's identity changes on every filter, sort and page change, and depending on
    // it would tear down the interval and fire an extra poll each time.
    const loadRef = useRef(load);
    useEffect(() => { loadRef.current = load; }, [load]);

    // Mirrored so the poll can spot a run ending without depending on sendJob. Synced
    // from state rather than tracked inside the poll, so the optimistic set after a
    // dispatch and the 409 handler both count as the run going live.
    const isSendLiveRef = useRef(isSendLive);
    useEffect(() => { isSendLiveRef.current = isSendLive; }, [isSendLive]);

    const isMountedRef = useIsMounted();
    const pollSendStatus = useCallback(async () => {
        try {
            const { job_run } = await soaApi.getSendStatus();
            if (!isMountedRef.current) return;
            // Outside the updater: load() sets state and fetches, an updater has to
            // be pure, and StrictMode double-invokes it — which would reload twice.
            const wasLive = isSendLiveRef.current;
            setSendJob(job_run);
            if (wasLive && !job_run) loadRef.current();
        } catch {
            // A failed poll is not worth a toast; the next tick retries.
        }
    }, [isMountedRef]);
    // The banner and the Send button both sit behind canSendEmail, so without it
    // nothing renders what this returns. Paused while the tab is hidden.
    useVisiblePolling(pollSendStatus, isSendLive ? 10000 : 60000, { enabled: canSendEmail, immediate: true });

    // Full Report has no disclaimer and downloads immediately. 2 Pages + Disclaimer
    // opens a context-edit step first — the same picker the send modal uses, since
    // this download exists specifically to preview what a send would attach.
    const handleDownloadReport = useCallback((document: SoaDocument, variant: SoaReportVariant) => {
        if (variant === 'two_page_disclaimer') {
            setDownloadMode(defaultDisclaimerMode(disclaimerStatus));
            setDownloadText(disclaimerStatus?.text ?? '');
            setDownloadDialogDocument(document);
            return;
        }
        const base = document.original_filename.replace(/\.pdf$/i, '');
        return runDownload(
            () => soaApi.downloadReport(document.id, variant),
            `${base}_${REPORT_VARIANT_LABEL[variant]}.pdf`,
            'Could not generate this report.',
        );
    }, [disclaimerStatus]);

    const handleConfirmDownload = async () => {
        if (!downloadDialogDocument) return;
        setDownloading(true);
        try {
            if (downloadMode === 'text') {
                setDisclaimerStatus(await soaApi.saveDisclaimerText(downloadText));
            }
            const base = downloadDialogDocument.original_filename.replace(/\.pdf$/i, '');
            const succeeded = await runDownload(
                () => soaApi.downloadReport(downloadDialogDocument.id, 'two_page_disclaimer', downloadMode),
                `${base}_${REPORT_VARIANT_LABEL.two_page_disclaimer}.pdf`,
                'Could not generate this report.',
            );
            if (succeeded) setDownloadDialogDocument(null);
        } catch (err) {
            Swal.fire('Could not save disclaimer text', getErrorMessage(err, 'Please try again.'), 'error');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadEmailLog = useCallback((document: SoaDocument) => {
        const base = document.original_filename.replace(/\.pdf$/i, '');
        return runDownload(
            () => soaApi.downloadEmailLog(document.id),
            `${base}_email_log.csv`,
            'Could not download the email log.',
        );
    }, []);

    const handleDownloadEmailLogsBulk = useCallback(() => runDownload(
        () => soaApi.downloadEmailLogsBulk(filterParams),
        `soa_email_logs_${month || 'all'}.csv`,
        'Could not download the email logs.',
    ), [filterParams, month]);

    // Bounces are read from the mailbox, not pushed to us — hence a deliberate action.
    const handleCheckEmailStatus = useCallback(async () => {
        setCheckingStatus(true);
        try {
            const summary = await soaApi.checkEmailStatus();
            const headline = summary.bounced_count
                ? `${summary.bounced_count} statement(s) came back undelivered and are now marked Bounced.`
                : `Checked ${summary.scanned} delivery report(s). Nothing sent recently has bounced.`;
            await Swal.fire({
                title: summary.bounced_count ? 'Bounces found' : 'No bounces found',
                html: `${headline}<br/><br/><small>A bad address is reported within minutes. A temporary failure can take up to a day, so check again later if a client reports not receiving theirs.</small>`,
                icon: summary.bounced_count ? 'warning' : 'success',
            });
            if (summary.bounced_count) load();
        } catch (err) {
            Swal.fire('Check failed', getErrorMessage(err, 'Could not read the mailbox.'), 'error');
        } finally {
            setCheckingStatus(false);
        }
    }, [load]);

    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, document: SoaDocument) => {
        setMenu({ anchorEl: event.currentTarget, document });
    }, []);

    const handleCloseMenu = useCallback(() => {
        setMenu(null);
    }, []);

    const handleDelete = useCallback(async (document: SoaDocument) => {
        const confirmed = await Swal.fire({
            title: 'Delete this SOA?',
            text: document.original_filename,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            ...confirmColors('danger'),
        });
        if (!confirmed.isConfirmed) return;

        try {
            await soaApi.deleteDocument(document.id);
            load();
        } catch (err) {
            Swal.fire('Delete failed', getErrorMessage(err, 'Could not delete the file.'), 'error');
        }
    }, [load]);

    const toggleSelected = useCallback((document: SoaDocument) => {
        setSelectedById(previous => {
            const next = new Map(previous);
            if (next.has(document.id)) {
                next.delete(document.id);
            } else {
                next.set(document.id, document);
            }
            return next;
        });
    }, []);

    const selectedDocuments = useMemo(() => [...selectedById.values()], [selectedById]);

    // Every statement matching the current filters, not just the visible page.
    const selectAllMatching = async () => {
        setSelectingAll(true);
        try {
            const CHUNK = 500;
            const MAX_PAGES = 200;
            const picked = new Map<number, SoaDocument>();
            for (let page = 1; page <= MAX_PAGES; page += 1) {
                const response = await soaApi.getDocuments({
                    ...filterParams,
                    page,
                    page_size: CHUNK,
                });
                response.results.forEach(document => picked.set(document.id, document));
                // The empty-page guard stops a stale count from looping forever.
                if (picked.size >= response.count || response.results.length === 0) break;
            }
            setSelectedById(picked);
        } catch (err) {
            Swal.fire('Could not select', getErrorMessage(err, 'Please try again.'), 'error');
        } finally {
            setSelectingAll(false);
        }
    };

    // Selected but not eligible to send at all — anything short of fully verified
    // must be fixed (or the client reassigned) before this batch goes out.
    const unverifiedCount = useMemo(
        () => selectedDocuments.filter(
            document => document.client_master && document.verification_status !== ALL_VERIFIED,
        ).length,
        [selectedDocuments],
    );
    // Recomputed while the send modal's subject/body inputs are typed into, since
    // those live in this component's state — memoized so a keystroke doesn't re-scan
    // the selection.
    const unassignedCount = useMemo(
        () => selectedDocuments.filter(document => !document.client_master).length,
        [selectedDocuments],
    );
    const alreadySentCount = useMemo(
        () => selectedDocuments.filter(document => document.email_status === 'sent').length,
        [selectedDocuments],
    );

    const openSendModal = async () => {
        setSendRecipients(isTestMode ? (user?.email ?? '') : '');
        setForce(false);
        // Defaults to the far more common case; the sender picks Quarterly explicitly
        // for the four months that need it.
        setQuarterly(false);
        setDisclaimerMode(defaultDisclaimerMode(disclaimerStatus));
        setDisclaimerText(disclaimerStatus?.text ?? '');
        setSendOpen(true);
        try {
            const template = await soaApi.getEmailTemplate();
            setSendSubject(template.subject);
            setSendBody(template.body);
            setTemplateVariables(template.available_variables);
        } catch (err) {
            setSendOpen(false);
            Swal.fire('Could not load template', getErrorMessage(err, 'Please try again.'), 'error');
        }
    };

    const handleSend = async () => {
        setSendOpen(false);
        setSending(true);
        // Three chained requests before the result dialog appears, and the modal has
        // already closed. Non-blocking on purpose: the page no longer holds a
        // multi-minute request, so there is nothing left to gate.
        const startingToast = toast.info('Starting send…', { autoClose: false });
        try {
            await soaApi.updateEmailTemplate(sendSubject, sendBody);
            if (!quarterly && disclaimerMode === 'text') {
                setDisclaimerStatus(await soaApi.saveDisclaimerText(disclaimerText));
            }
            const summary = await soaApi.sendEmails(
                [...selectedById.keys()],
                quarterly,
                force,
                sendRecipients.trim() || undefined,
                quarterly ? undefined : disclaimerMode,
            );

            const detail = [];
            if (summary.queued) {
                detail.push(
                    summary.test_recipient
                        ? `<b>Test mode</b> — ${summary.queued} message(s) will go to ${summary.test_recipient}.`
                        : `${summary.queued} statement(s) queued for sending.`,
                );
                // The run outlives this page, so the row status is the progress view.
                detail.push(
                    '<small>This runs in the background and takes a few minutes. '
                    + 'Refresh to watch the Email Status column, and you will be notified '
                    + 'when it finishes. After that, use <b>Check Email Status</b> to see if '
                    + 'any bounced — a report that arrives before the run ends shows nothing '
                    + 'and reads as an all-clear.</small>',
                );
            }
            if (summary.skipped) {
                const reasons = Object.entries(summary.skipped_reasons)
                    .map(([reason, count]) => `${count} ${reason.replace(/_/g, ' ')}`)
                    .join(', ');
                detail.push(`${summary.skipped} skipped (${reasons})`);
            }
            if (summary.job_run_id) {
                setSendJob({
                    id: summary.job_run_id,
                    status: 'queued',
                    started_ts: new Date().toISOString(),
                    started_by: user?.email ?? null,
                    test_recipient: summary.test_recipient,
                    sent: 0,
                    failed: 0,
                    total: summary.queued,
                    skipped: summary.skipped,
                });
            }

            await Swal.fire({
                title: summary.queued ? 'Sending started' : 'Nothing to send',
                html: detail.join('<br/>'),
                icon: summary.queued ? 'success' : 'info',
            });
            setSelectedById(new Map());
            load();
        } catch (err) {
            // 409 means another send holds the lock — a refused duplicate, not a
            // failure, so show what is running rather than an error the sender has to
            // interpret.
            const running = axios.isAxiosError(err) && err.response?.status === 409
                ? (err.response.data?.job_run as SoaSendJobState | null)
                : null;
            if (running) {
                setSendJob(running);
                Swal.fire({
                    title: 'A send is already running',
                    // text, not html: the line carries no markup, and started_by is a
                    // stored address rather than anything this component controls.
                    text: `Started${running.started_by ? ` by ${running.started_by}` : ''}`
                        + ` at ${formatDateTime(running.started_ts)}.`
                        + ' Wait for it to finish, then send the rest.',
                    icon: 'info',
                });
            } else {
                Swal.fire('Send failed', getErrorMessage(err, 'Could not start the send.'), 'error');
            }
        } finally {
            toast.dismiss(startingToast);
            setSending(false);
        }
    };

    const handleAssigned = (warning?: string) => {
        if (warning) {
            Swal.fire('Assigned, but unverified', warning, 'warning');
        }
        load();
    };

    const handleView = useCallback(async (row: SoaDocument) => {
        // Both the popup and the clipboard write need the click's user activation,
        // which expires across an await — so start them BEFORE fetching the file.
        const tab = window.open('', '_blank');
        const copying = row.pan_card
            ? navigator.clipboard.writeText(row.pan_card).then(() => true, () => false)
            : Promise.resolve(false);

        try {
            // Via the API client so the JWT is attached — the GCS bucket is private,
            // so opening row.file directly would fail.
            const blob = await soaApi.download(row.id);
            const url = window.URL.createObjectURL(blob);
            if (tab) {
                tab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        } catch (err) {
            tab?.close();
            Swal.fire('Could not open', getErrorMessage(err, 'Failed to load the statement.'), 'error');
            return;
        }

        if (!row.pan_card) return;
        Swal.fire(await copying
            ? {
                icon: 'success',
                title: 'PAN copied',
                text: 'Paste it as the PDF password.',
                timer: 1200,
                showConfirmButton: false,
            }
            : {
                icon: 'info',
                title: 'PDF password',
                text: `Could not copy automatically. The password is ${row.pan_card}`,
            });
    }, []);
    const columns = useMemo((): ColumnDef<SoaDocument>[] => [
        ...(canSendEmail ? [{
            key: 'select', header: 'Send', width: '60px',
            render: (r: SoaDocument) => (
                <input
                    type="checkbox"
                    aria-label={`Select ${r.original_filename}`}
                    checked={selectedById.has(r.id)}
                    // Unassigned has no recipient. An inactive client still gets their
                    // statement — a closed account is owed its final one.
                    disabled={!r.client_master}
                    onChange={() => toggleSelected(r)}
                />
            ),
        }] : []),
        {
            key: 'period_end', header: 'Month', sortable: true, width: '110px',
            render: r => formatDate(r.period_end),
        },
        {
            key: 'original_filename', header: 'File', sortable: true, width: '340px',
            render: r => <span className={styles['file-name']}>{r.original_filename}</span>,
        },
        {
            key: 'client_master__first_name', header: 'Client', sortable: true, width: '240px',
            render: r => (
                <div className={styles['client-cell']}>
                    {r.client_name
                        ? <span>{r.client_name}</span>
                        : <Pill tone="warning">Unassigned</Pill>}
                    {r.client_code && (
                        <span className={styles['client-code']}>
                            {r.client_code}
                            {/* null when unassigned — neither tag applies then. */}
                            {r.is_client_active !== null && (
                                <ActiveStatus active={!!r.is_client_active} />
                            )}
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'to_emails', header: 'Client Email', width: '240px',
            render: r => {
                if (!r.client_master) return '—';
                if (!r.to_emails.length) {
                    // The send would skip this row with reason "no_recipient".
                    return <Pill tone="warning">No email</Pill>;
                }
                return <span className={styles.emails}>{r.to_emails.join(', ')}</span>;
            },
        },
        {
            key: 'cc_emails', header: 'CC — Distributor / RM', width: '200px',
            render: r => (r.client_master
                ? (
                    <div className={styles['cc-cell']}>
                        {renderCcEmail('Distributor', r.distributor_email)}
                        {renderCcEmail('RM', r.distributor_rm_email)}
                    </div>
                )
                : '—'),
        },
        {
            key: 'verification_status', header: 'Verified', sortable: true, width: '180px',
            render: r => {
                if (!r.client_master) return '—';
                if (!r.missing_verifications.length) {
                    return <Pill tone="positive">Verified</Pill>;
                }
                // Naming what failed saves opening the PDF to find out which check it was.
                return (
                    <Tooltip title="PAN opens the PDF → Account No. on page 1 matches → client email is valid">
                        <span>
                            <Pill tone="warning">Missing: {r.missing_verifications.join(', ')}</Pill>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            key: 'email_status', header: 'Email Status', sortable: true, width: '140px',
            render: r => {
                if (r.email_status === 'sent') {
                    return (
                        <Pill tone={emailStatusTone(r.email_status)}>
                            {r.send_ts ? formatDate(r.send_ts) : r.email_status_label}
                        </Pill>
                    );
                }
                if (r.email_status === 'failed') {
                    return (
                        <Tooltip title={r.last_email_error || 'The last send attempt failed.'}>
                            <span><Pill tone={emailStatusTone(r.email_status)}>{r.email_status_label}</Pill></span>
                        </Tooltip>
                    );
                }
                if (r.email_status === 'bounced') {
                    return (
                        <Tooltip title="Delivered to the mail server, then returned undelivered. Check the client's email address — this will be re-sent in the next batch.">
                            <span><Pill tone={emailStatusTone(r.email_status)}>{r.email_status_label}</Pill></span>
                        </Tooltip>
                    );
                }
                return <Pill tone={emailStatusTone(r.email_status)}>{r.email_status_label}</Pill>;
            },
        },
        {
            key: 'actions', header: 'Actions', width: '70px',
            render: r => (
                <Tooltip title="Actions">
                    <IconButton
                        size="small"
                        aria-label={`Actions for ${r.original_filename}`}
                        onClick={event => handleOpenMenu(event, r)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [handleOpenMenu, selectedById, toggleSelected, canSendEmail]);

    // Last month is the default, not an applied filter — otherwise "Clear All" would
    // show permanently on a freshly-loaded page.
    const hasActiveFilters = !!(
        month !== previousMonthValue() || matchStatus || verificationStatus || emailStatusFilter
        || clientActive || selectedClient || selectedScheme || searchQuery.trim()
    );

    const handleClearFilters = () => {
        setMonth(previousMonthValue());
        setMatchStatus('');
        setVerificationStatus('');
        setEmailStatusFilter('');
        setClientActive('');
        setSelectedClient(null);
        setSelectedScheme(null);
        onSearchChange('');
        resetPage();
    };

    return (
        <>
            <DataTable
                title="Summary of Account (SOA)"
                subtitle="Monthly statements per client, and the state of each send."
                data={documents}
                columns={columns}
                rowKey={r => r.id}
                totalCount={totalCount}
                loading={loading}
                error={error}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                pageSize={pageSize}
                sortField={sortField}
                sortDir={sortDir}
                onSort={onSort}
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
                searchPlaceholder="Search by client name, client code, FA account no, or file name…"
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                emptyMessage="No SOA documents found for these filters."
                filterSlot={
                    <>
                        <div className={shared['filter-group']}>
                            <label htmlFor="soa-month">Statement Month</label>
                            <input
                                id="soa-month"
                                type="month"
                                className={shared.input}
                                value={month}
                                onChange={event => { setMonth(event.target.value); resetPage(); }}
                            />
                        </div>

                        <FilterSelect
                            label="Assignment"
                            value={MATCH_STATUS_OPTIONS.find(o => o.value === matchStatus) ?? null}
                            onChange={opt => { setMatchStatus(opt?.value ?? ''); resetPage(); }}
                            options={MATCH_STATUS_OPTIONS}
                            placeholder="All Documents"
                        />

                        <FilterSelect
                            label="Verification"
                            value={VERIFICATION_OPTIONS.find(o => o.value === verificationStatus) ?? null}
                            onChange={opt => { setVerificationStatus(opt?.value ?? ''); resetPage(); }}
                            options={VERIFICATION_OPTIONS}
                        />

                        <FilterSelect
                            label="Email Status"
                            value={EMAIL_STATUS_OPTIONS.find(o => o.value === emailStatusFilter) ?? null}
                            onChange={opt => { setEmailStatusFilter(opt?.value ?? ''); resetPage(); }}
                            options={EMAIL_STATUS_OPTIONS}
                        />

                        <FilterSelect
                            label="Client Status"
                            value={CLIENT_ACTIVE_OPTIONS.find(o => o.value === clientActive) ?? null}
                            onChange={opt => { setClientActive(opt?.value ?? ''); resetPage(); }}
                            options={CLIENT_ACTIVE_OPTIONS}
                        />

                        <FilterAsyncSelect
                            label="Scheme"
                            value={selectedScheme}
                            onChange={option => { setSelectedScheme(option); resetPage(); }}
                            loadOptions={loadSchemeOptions}
                            placeholder="All Schemes"
                        />

                        <FilterAsyncSelect
                            label="Client"
                            value={selectedClient}
                            onChange={option => { setSelectedClient(option); resetPage(); }}
                            loadOptions={loadClientOptions}
                            placeholder="All Clients"
                        />
                    </>
                }
                toolbarSlot={canSendEmail && (
                    <div className={styles.toolbar}>
                        <SoaDisclaimerBar onStatusChange={setDisclaimerStatus} />
                        {sendJob && (
                            <div className={styles['send-progress']}>
                                <span>
                                    {sendJob.test_recipient ? 'Test send' : 'Sending'} in progress
                                    {sendJob.started_by ? ` (${sendJob.started_by})` : ''} —{' '}
                                    <b>{sendJob.sent}</b> of {sendJob.total || '?'} sent
                                    {sendJob.failed > 0 && `, ${sendJob.failed} failed`}
                                </span>
                                <span className={styles['send-progress-track']}>
                                    <span
                                        className={styles['send-progress-fill']}
                                        style={{ width: sendJob.total
                                            ? `${Math.round((sendJob.sent / sendJob.total) * 100)}%`
                                            : '0%' }}
                                    />
                                </span>
                            </div>
                        )}
                        {selectedById.size > 0 && (
                            <span className={styles['selection-count']}>
                                {selectedById.size} selected
                            </span>
                        )}
                        <div className={styles['toolbar-actions']}>
                            <ButtonTip
                                title={selectedById.size > 0
                                    ? 'Clear the current selection'
                                    : 'Select every statement matching these filters, across all pages'}
                            >
                                <Button
                                    variant="secondary"
                                    disabled={selectingAll || totalCount === 0}
                                    onClick={selectedById.size > 0
                                        ? () => setSelectedById(new Map())
                                        : selectAllMatching}
                                >
                                    {selectingAll && 'Selecting…'}
                                    {!selectingAll && (selectedById.size > 0 ? 'Clear' : 'Select All')}
                                </Button>
                            </ButtonTip>

                            <ButtonTip
                                title={sendJob
                                    ? 'A send is already running — only one runs at a time'
                                    : selectedById.size === 0
                                        ? 'Tick a row, or use Select All, to choose what to send'
                                        : `Email the ${selectedById.size} selected statement(s)`}
                            >
                                <Button
                                    variant="success"
                                    disabled={selectedById.size === 0 || sending || !!sendJob}
                                    onClick={openSendModal}
                                >
                                    {sending && 'Sending…'}
                                    {!sending && (sendJob ? 'Send running…' : 'Send')}
                                </Button>
                            </ButtonTip>
                        </div>
                    </div>
                )}
                headerActions={
                    <>
                        {canSendEmail && (
                            <ButtonTip title="Read the mailbox for delivery failures and mark those statements as bounced">
                                <Button
                                    variant="secondary"
                                    disabled={checkingStatus}
                                    onClick={handleCheckEmailStatus}
                                >
                                    {checkingStatus ? 'Checking…' : 'Check Status'}
                                </Button>
                            </ButtonTip>
                        )}
                        <ButtonTip title="Download every send attempt for the current filters as a CSV">
                            <Button
                                variant="secondary"
                                onClick={handleDownloadEmailLogsBulk}
                            >
                                Email Logs
                            </Button>
                        </ButtonTip>
                        <ButtonTip
                            title={month
                                ? 'Active clients with no statement for this month'
                                : 'Pick a statement month first'}
                        >
                            <Button
                                variant="secondary"
                                disabled={!month}
                                onClick={() => setMissingOpen(true)}
                            >
                                Not Received
                            </Button>
                        </ButtonTip>
                        <ButtonTip title="Upload a PDF, or a zip of statements, for one month">
                            <Button
                                variant="primary"
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload
                            </Button>
                        </ButtonTip>
                    </>
                }
            />

            <SoaRowActionsMenu
                anchorEl={menu?.anchorEl ?? null}
                onClose={handleCloseMenu}
                document={menu?.document ?? null}
                onView={handleView}
                onDownloadReport={handleDownloadReport}
                onDownloadEmailLog={handleDownloadEmailLog}
                onAssign={setAssigning}
                onDelete={handleDelete}
            />

            <SoaMissingClientsDialog
                open={missingOpen}
                periodEnd={month ? monthEndDate(month) : ''}
                schemeId={selectedScheme ? parseInt(selectedScheme.value, 10) : undefined}
                onClose={() => setMissingOpen(false)}
            />

            <SoaUploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUploaded={summary => {
                    Swal.fire('Upload complete', describeUpload(summary), 'success');
                    load();
                }}
            />

            <SoaAssignDialog
                document={assigning}
                onClose={() => setAssigning(null)}
                onAssigned={handleAssigned}
            />

            {sendOpen && (
                <SoaSendModal
                    selectedCount={selectedDocuments.length}
                    unassignedCount={unassignedCount}
                    alreadySentCount={alreadySentCount}
                    unverifiedCount={unverifiedCount}
                    isTestMode={isTestMode}
                    quarterly={quarterly}
                    onQuarterlyChange={setQuarterly}
                    disclaimerStatus={disclaimerStatus}
                    onDisclaimerStatusChange={setDisclaimerStatus}
                    disclaimerMode={disclaimerMode}
                    onDisclaimerModeChange={setDisclaimerMode}
                    disclaimerText={disclaimerText}
                    onDisclaimerTextChange={setDisclaimerText}
                    subject={sendSubject}
                    onSubjectChange={setSendSubject}
                    body={sendBody}
                    onBodyChange={setSendBody}
                    availableVariables={templateVariables}
                    recipients={sendRecipients}
                    onRecipientsChange={setSendRecipients}
                    force={force}
                    onForceChange={setForce}
                    sending={sending}
                    onCancel={() => setSendOpen(false)}
                    onConfirm={handleSend}
                />
            )}

            {downloadDialogDocument && (
                <SoaDownloadDisclaimerDialog
                    document={downloadDialogDocument}
                    mode={downloadMode}
                    onModeChange={setDownloadMode}
                    text={downloadText}
                    onTextChange={setDownloadText}
                    disclaimerStatus={disclaimerStatus}
                    onDisclaimerStatusChange={setDisclaimerStatus}
                    downloading={downloading}
                    onCancel={() => setDownloadDialogDocument(null)}
                    onConfirm={handleConfirmDownload}
                />
            )}
        </>
    );
};

export default SoaList;
